import Phaser from "phaser";
import {
    Action, ActionType, effectiveSuit, EngineEvent, EventType, Phase, PlayerIndex, PLAYERS, Rank, State,
    SUITS
} from "@ravalmatic/euchre-engine";
import { GameConstants } from "../../GameConstants";
import { GameManager } from "../../GameManager";
import { Preferences } from "../../Preferences";
import { AudioManager } from "../../AudioManager";
import { addBackground, button, fitLayout, label, Layout } from "../../ui/Layout";
import { BoardManager, BoardSnapshot } from "./BoardManager";
import { Card } from "./Card";
import { GUI } from "./gui/GUI";

const { LOCAL_PLAYER, PLAYER_NAMES, TEAM_NAMES } = GameConstants;

export class BoardScene extends Phaser.Scene {
    private root!: Phaser.GameObjects.Container;
    private content!: Phaser.GameObjects.Container;
    private manager!: BoardManager;
    private snapshot?: BoardSnapshot;
    private alone = false;
    private previousHand = 0;
    private layout!: Layout;
    private gui!: GUI;

    constructor() { super("BoardScene"); }

    public create(): void {
        GameManager.setCurrentScene(this);
        this.alone = false;
        this.previousHand = 0;
        this.snapshot = undefined;
        this.root = this.add.container(0, 0);
        this.content = new Phaser.GameObjects.Container(this);
        this.root.add(this.content);
        this.gui = new GUI(this);
        this.add.existing(this.gui);
        this.manager = new BoardManager(
            crypto.getRandomValues(new Uint32Array(1))[0], Preferences.difficulty,
            (delay, callback) => {
                const timer = this.time.delayedCall(delay, callback);
                return () => timer.remove();
            },
            snapshot => {
                this.snapshot = snapshot;
                this.render();
            }
        );
        for (const type of [EventType.CARD_PLAYED, EventType.DEAL_EXECUTED, EventType.GAME_OVER]) {
            this.manager.engine.addEventListener(type, this.onEngineSound, this);
        }
        this.scale.on("resize", this.render, this);
        this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
            this.manager.destroy();
            for (const type of [EventType.CARD_PLAYED, EventType.DEAL_EXECUTED, EventType.GAME_OVER]) {
                this.manager.engine.removeEventListener(type, this.onEngineSound, this);
            }
            this.scale.off("resize", this.render, this);
            this.time.timeScale = 1;
            this.snapshot = undefined;
        });
        this.manager.start();
    }

    private onEngineSound(event: EngineEvent): void {
        if (event.type === EventType.CARD_PLAYED) AudioManager.playEffect("card_move");
        else if (event.type === EventType.DEAL_EXECUTED) AudioManager.playEffect("card_flip");
        else AudioManager.playEffect(event.state.winningTeam === 0 ? "win" : "fail1");
    }

    private render(): void {
        if (!this.snapshot) return;
        this.layout = fitLayout(this, this.root);
        this.gui.relayout();
        const { width, height, landscape } = this.layout;
        this.content.removeAll(true);
        this.content.add(addBackground(this, landscape ? "bg_1" : "bg_1_portrait", width, height));
        this.content.add(new Phaser.GameObjects.Rectangle(this, width / 2, height / 2, width, height, 0x2A0B20, .35));
        const { state, busy } = this.snapshot;
        if (state.handNumber !== this.previousHand) {
            this.alone = false;
            this.previousHand = state.handNumber;
        }
        this.drawHeader(state);
        for (const player of PLAYERS) this.drawPlayer(player, state);
        if (state.phase === Phase.BIDDING) {
            this.drawBidding(state);
        } else {
            this.drawTrick(state);
        }
        const ended = state.phase === Phase.HAND_OVER || state.phase === Phase.GAME_OVER;
        if (ended && !busy) this.drawResult(state);
        if (this.snapshot.error) {
            label(this, this.content, width / 2, height - 60, this.snapshot.error, 24)
                .setWordWrapWidth(width - 80);
        } else {
            label(this, this.content, width / 2, height - 28, "You + North  vs  West + East  |  First to 10", 22);
        }
    }

    private drawHeader(state: State): void {
        const { width, landscape } = this.layout;
        const header = new Phaser.GameObjects.Graphics(this);
        header.fillStyle(0x2A0B20, .9);
        header.fillRoundedRect(landscape ? 200 : 10, 10, landscape ? width - 400 : width - 20, landscape ? 105 : 125, 16);
        this.content.add(header);
        label(this, this.content, width / 2, landscape ? 37 : 40,
            `${TEAM_NAMES[0]}  ${state.scores[0]}   :   ${state.scores[1]}  ${TEAM_NAMES[1]}`, 32);
        const contract = state.trump
            ? `Trump: ${state.trump.toUpperCase()}  |  ${PLAYER_NAMES[state.makerIndex!]}${state.alone ? " alone" : ""}  |  Tricks ${state.tricks[0]} : ${state.tricks[1]}`
            : `Choose trump  |  Round ${state.biddingRound}  |  Stick the dealer`;
        label(this, this.content, width / 2, landscape ? 83 : 89,
            `Hand ${state.handNumber}  |  ${contract}`, landscape ? 25 : 23)
            .setWordWrapWidth(landscape ? width - 440 : width - 70);
    }

    private drawPlayer(player: PlayerIndex, state: State): void {
        const { width, height, landscape } = this.layout;
        const active = player === state.currentPlayerIndex
            && ![Phase.HAND_OVER, Phase.GAME_OVER].includes(state.phase);
        const inactive = player === state.inactivePlayerIndex;
        const x = player === 1 ? (landscape ? 200 : 120)
            : player === 3 ? width - (landscape ? 200 : 120) : width / 2;
        const y = player === 0 ? (landscape ? 660 : 935)
            : player === 2 ? (landscape ? 155 : 175) : (landscape ? 330 : 360);
        const status = `${PLAYER_NAMES[player]}${player === 2 ? " / partner" : ""}${state.dealerIndex === player ? " / dealer" : ""}`;
        label(this, this.content, x, y, status, landscape ? 29 : 26)
            .setFontStyle(active ? "bold" : "normal");
        if (inactive) label(this, this.content, x, y + 34, "Sitting out", 22);
        else if (state.phase === Phase.BIDDING && this.snapshot!.bids[player]) {
            label(this, this.content, x, y + 34, this.snapshot!.bids[player], 22);
        }
        const hand = [...state.hands[player]].sort((a, b) => SUITS.indexOf(a.suit) - SUITS.indexOf(b.suit) || b.rank - a.rank);
        const scale = player === 0 ? .9 : player === 2 ? .46 : (landscape ? .5 : .38);
        const step = player === 0 ? 118 : player === 2 ? 35 : (landscape ? 35 : 22);
        const handY = player === 0 ? (landscape ? 775 : height - 145)
            : y + (player === 2 ? 78 : 110);
        const playable = this.snapshot!.legalActions.filter(
            (action): action is Extract<Action, { cardId: number }> => action.type === ActionType.PLAY_CARD || action.type === ActionType.DISCARD
        );
        hand.forEach((card, index) => {
            const enabled = player === LOCAL_PLAYER && !this.snapshot!.busy && playable.some(action => action.cardId === card.id);
            const visible = player === LOCAL_PLAYER;
            const cardView = new Card(this, visible ? card : null,
                enabled ? () => this.manager.selectCard(card.id) : undefined,
                visible && !enabled && state.phase === Phase.PLAYING
            ).setPosition(x + (index - (hand.length - 1) / 2) * step, handY).setScale(scale);
            if (inactive) cardView.setAlpha(.35);
            this.content.add(cardView);
            if (visible && state.trump && card.rank === Rank.JACK && card.suit !== state.trump
                && effectiveSuit(card, state.trump) === state.trump) {
                label(this, this.content, cardView.x, handY + 100, "TRUMP", 18);
            }
        });
    }

    private drawBidding(state: State): void {
        const { width, landscape } = this.layout;
        const center = width / 2;
        const myTurn = state.currentPlayerIndex === LOCAL_PLAYER;
        const upcardY = landscape ? 345 : 370;
        label(this, this.content, center + 160, upcardY,
            state.biddingRound === 1 ? "UPCARD" : "TURNED DOWN", 24);
        if (state.upcard) {
            this.content.add(new Card(this, state.upcard).setPosition(center, upcardY).setScale(.65)
                .setAlpha(state.biddingRound === 1 ? 1 : .65));
        }
        label(this, this.content, center, upcardY + 105,
            myTurn ? (state.biddingRound === 1 ? "Order up the dealer, or pass" : "Choose another suit")
                : `${PLAYER_NAMES[state.currentPlayerIndex]} is choosing trump...`, 28);
        if (!myTurn) return;
        const actions = this.snapshot!.legalActions;
        const y = landscape ? 530 : 565;
        const pass = actions.find(action => action.type === ActionType.PASS);
        if (state.biddingRound === 1) {
            button(this, this.content, center - 120, y, "Order up", () => this.manager.submit({
                type: ActionType.ORDER_UP, playerIndex: LOCAL_PLAYER, alone: this.alone
            }));
            button(this, this.content, center + 120, y, "Pass", () => this.manager.submit({
                type: ActionType.PASS, playerIndex: LOCAL_PLAYER
            }));
        } else {
            const suits = SUITS.filter(suit => actions.some(action => action.type === ActionType.CALL_TRUMP && action.suit === suit));
            suits.forEach((suit, index) => {
                button(this, this.content, center + (index - 1) * 195, y, suit.toUpperCase(), () => this.manager.submit({
                    type: ActionType.CALL_TRUMP, playerIndex: LOCAL_PLAYER, suit, alone: this.alone
                }), 180);
            });
            button(this, this.content, center - 155, y + 70, pass ? "Pass" : "Dealer must choose", () => {
                this.manager.submit({ type: ActionType.PASS, playerIndex: LOCAL_PLAYER });
            }, 280, Boolean(pass));
        }
        button(this, this.content, center + (state.biddingRound === 1 ? 0 : 155), y + 70,
            `Go alone: ${this.alone ? "ON" : "OFF"}`, () => {
                this.alone = !this.alone;
                this.render();
            }, 260);
    }

    private drawTrick(state: State): void {
        const { width, landscape } = this.layout;
        const centerY = landscape ? 430 : 610;
        const displayed = this.snapshot!.displayedTrick;
        const plays = displayed?.plays ?? state.currentTrick;
        const positions = [[0, 105], [-145, 0], [0, -105], [145, 0]];
        for (const play of plays) {
            const [dx, dy] = positions[play.playerIndex];
            const view = new Card(this, play.card)
                .setPosition(width / 2 + dx, centerY + dy).setScale(.65);
            this.content.add(view);
            if (displayed?.winnerIndex === play.playerIndex) {
                const border = new Phaser.GameObjects.Graphics(this);
                border.lineStyle(4, 0x89e7a5);
                border.strokeRoundedRect(view.x - 45, view.y - 63, 90, 126, 8);
                this.content.add(border);
            }
        }
        const message = displayed ? `${PLAYER_NAMES[displayed.winnerIndex]} wins the trick`
            : state.phase === Phase.DISCARD ? state.currentPlayerIndex === LOCAL_PLAYER
                ? "Choose one card to discard" : `${PLAYER_NAMES[state.dealerIndex]} is discarding...`
            : state.phase === Phase.PLAYING ? state.currentPlayerIndex === LOCAL_PLAYER
                ? "Your turn: choose a highlighted card" : `${PLAYER_NAMES[state.currentPlayerIndex]} is playing...` : "";
        label(this, this.content, width / 2, landscape ? 610 : 845, message, 28);
        if (state.phase === Phase.DISCARD && state.upcard) {
            label(this, this.content, width / 2, centerY - 110, "DEALER PICKS UP", 25);
            this.content.add(new Card(this, state.upcard).setPosition(width / 2, centerY).setScale(.65));
        }
    }

    private drawResult(state: State): void {
        const result = state.lastResult;
        if (!result) return;
        const { width, landscape } = this.layout;
        const y = landscape ? 410 : 590;
        const layer = new Phaser.GameObjects.Container(this, width / 2, y);
        layer.add(new Phaser.GameObjects.Rectangle(this, 0, 0, 720, 390, 0x2A0B20, .97).setStrokeStyle(3, 0x6cc9ce));
        const gameOver = state.phase === Phase.GAME_OVER;
        const winner = gameOver ? state.winningTeam! : result.winningTeam;
        label(this, layer, 0, -140, gameOver ? `${TEAM_NAMES[winner]} win!` : result.euchred ? "EUCHRED!" : "HAND COMPLETE", 42);
        label(this, layer, 0, -65, `${TEAM_NAMES[result.winningTeam]}  +${result.points}`, 34);
        label(this, layer, 0, -10,
            `${PLAYER_NAMES[result.makerIndex]}${result.alone ? " alone" : ""}  |  Tricks ${result.tricks[0]} : ${result.tricks[1]}`, 27);
        label(this, layer, 0, 40, `Score  ${state.scores[0]} : ${state.scores[1]}`, 32);
        button(this, layer, 0, 125, gameOver ? "New match" : "Next hand", () => {
            if (gameOver) this.scene.restart();
            else this.manager.submit({ type: ActionType.DEAL });
        }, 260);
        this.content.add(layer);
    }

}
